import { useState, useEffect } from "react";
import { AxiosError } from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import EditCoursModal from "./editCoursModal";
import CreateCoursModal from "./createCoursModal";
import { useAcademicYear } from "../../../core/common/context/AcademicYearContext";
import InfosAlertYearDisabled from "../../../core/common/alerts/infosAlertYearDisabled";
import withPermission from "../../../core/common/autorisations/withPermission";
import {
  Action,
  buildPermission,
  buildPermissionContext,
  Resource,
} from "../../../core/common/autorisations/permissionsMap";
import { getAllClass, getAllClassroom } from "../../../api/pedagogicElement";
import { deleteCourse, getCoursesByAcademicYear } from "../../../api/cours";
import CourseSupportList from "./courseSupportList";
import { Select } from "antd";
import { getAllEvaluationPeriod } from "../../../api/generalParam";
import { my_routes } from "../../router/all_routes";

// interface pour le type des données de la classe
interface ClassTable {
  id: string;
  label: string;
}

// Interface pour le type de données du cours
interface CoursTable {
  id: string;
  Subject: any;
  TeacherProfil: any;
  status: string;
  attachment?: string;
  createdAt: string;
  id_classes: string[];
  id_classroom?: string | null;
  class_assignments?: any[];
  classes?: Array<{ id: string; abbreviation: string; levelAbbreviation: string }>;
}

// Bouton protégé pour créer un cours
const ProtectedBtnAddCours = withPermission(
  (props: any) => (
    <div className="mb-2">
      <button
        onClick={() => {
          props.setShowCreate(true);
        }}
        className="btn btn-primary d-flex align-items-center"
      >
        <i className="ti ti-square-rounded-plus me-2" />
        Nouveau Cours
      </button>
    </div>
  ),
  [buildPermission(Resource.COURSE, Action.CREATE)],
  "masquer",
);

// Bouton protégé pour modifier un cours
const ProtectedBtnUpdateCours = withPermission(
  (props: any) => (
    <li>
      <Link
        className="dropdown-item rounded-1"
        to="#"
        onClick={() => {
          props.setSelectedCoursId(props.record.id);
          props.setShowEdit(true);
        }}
      >
        <i className="ti ti-edit-circle me-2" />
        Modifier
      </Link>
    </li>
  ),
  [buildPermission(Resource.COURSE, Action.UPDATE)],
  "masquer",
);

// Bouton protégé pour supprimer un cours
const ProtectedBtnDeleteCours = withPermission(
  (props: any) => (
    <li>
      <Link
        onClick={() => props.showConfirmationAlert(props.record)}
        className="dropdown-item rounded-1"
        to="#"
      >
        <i className="ti ti-trash-x me-2" />
        Supprimer
      </Link>
    </li>
  ),
  [buildPermission(Resource.COURSE, Action.DELETE)],
  "masquer",
);

const CoursList = () => {
  const { selectedYear } = useAcademicYear();
  const routes = my_routes;

  // Initialisation des state utilisés
  const [allCours, setAllCours] = useState<CoursTable[]>([]);
  const [filteredCours, setFilteredCours] = useState<CoursTable[]>([]);

  const [selectedCoursId, setSelectedCoursId] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);

  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showSupport, setShowSupport] = useState(false);

  const [allClass, setAllClass] = useState<ClassTable[]>([]);
  const [allClassroom, setAllClassroom] = useState<any[]>([]);

  const [selectedClass, setSelectedClass] = useState<ClassTable | undefined>();
  const [periods, setPeriods] = useState<any[]>([]);
  const [selectedPeriod, setSelectedPeriod] = useState<string>("all");
  const [searchTerm, setSearchTerm] = useState("");

  const [refreshKey, setRefreshKey] = useState(0);
  // Initialisation de sweetAlert
  const MySwal = withReactContent(Swal);

  // Traitement en alert de la demande de suppression de cours
  const showConfirmationAlert = (cours: CoursTable) => {
    MySwal.fire({
      title: "Supprimer un cours ?",
      text: `Souhaitez-vous vraiment supprimer le cours de ${
        cours.Subject?.label || "cette matière"
      } ?`,
      showCancelButton: true,
      confirmButtonText: "Confirmer",
      cancelButtonText: "Annuler",
    }).then(async (result) => {
      if (result.isConfirmed) {
        // ── Affiche le spinner pendant la requête ──
        MySwal.fire({
          title: "Suppression en cours...",
          allowOutsideClick: false,
          allowEscapeKey: false,
          showConfirmButton: false,
          didOpen: () => MySwal.showLoading(),
        });
        try {
          await deleteCourse(cours?.id);
          MySwal.fire({
            title: "Cours supprimé avec succès !",
            icon: "success",
            confirmButtonText: "OK",
          });

          reloadData(); // On reload la liste
        } catch (error) {
          const err = error as AxiosError<any>;
          // On affiche une alerte en cas d'erreur côté serveur
          MySwal.fire({
            title: "Erreur",
            text: err.response?.data?.message || err.response?.data?.error,
            icon: "error",
            confirmButtonText: "OK",
          });
        }
      }
    });
  };

  // Reload de la liste
  const reloadData = () => setRefreshKey((prev) => prev + 1);

  // fonction pour récupérer les classes
  const fetchAllClass = async () => {
    try {
      const response = await getAllClass(
        selectedYear?.id,
        buildPermissionContext(Resource.COURSE, Action.READ),
      );

      const formattedClass = response.data
        ?.slice() // pour éviter de modifier directement la réponse
        .sort((a: any, b: any) => {
          // Tri par Level.label
          const levelCompare = a.Level.label.localeCompare(
            b.Level.label,
            "fr",
            { numeric: true },
          );
          if (levelCompare !== 0) return levelCompare;

          // Si même Level, tri par class label
          return a.label.localeCompare(b.label, "fr", { numeric: true });
        })
        .map((item: any) => ({
          id: item.id,
          label: `${item?.Level?.abbreviation} - ${item.label}`,
        }));
      setAllClass(formattedClass || []);
    } catch (error: any) {
      // console.log("erreur de chargement des classes : ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  // fonction pour recuperer toutes les salles de classe de lannée académique sélectionnée
  const fetchAllClassroom = async () => {
    try {
      const response = await getAllClassroom(
        selectedYear?.id,
        buildPermissionContext(Resource.COURSE, Action.READ),
      );
      const formattedClassroom = response.data.map((classroom: any) => ({
        id: classroom.id,
        value: classroom.id,
        label: classroom.label,
      }));
      // console.log("Classroom: ", formattedClassroom);
      setAllClassroom(formattedClassroom);
    } catch (error: any) {
      // console.log("erreur de chargement des salles : ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  // fonction pour récupérer les cours
  const fetchCours = async () => {
    try {
      // TODO: Implémenter la fonction de récupération des cours
      const response = await getCoursesByAcademicYear(selectedYear?.id);
      const cours = response.data || [];
      setAllCours(cours);

      setHasLoaded(true);
      setIsLoading(false);
    } catch (error: any) {
      // console.log("erreur de chargement des cours : ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );

      // Option: reset les cours en cas d'erreur
      setAllCours([]);
    } finally {
      setIsLoading(false);
      setHasLoaded(true);
    }
  };

  // On récupère la liste des cours en appelant sa fonction
  useEffect(() => {
    setHasLoaded(false);
    setIsLoading(true);

    if (!selectedYear?.id) return;

    fetchAllClass();
    fetchAllClassroom();
    fetchCours();
    getAllEvaluationPeriod(selectedYear.id)
      .then((response) => setPeriods(response.data || []))
      .catch(() => setPeriods([]));
  }, [refreshKey, selectedYear]);

  // Filtrage combiné par classe, semestre et recherche.
  useEffect(() => {
    const filtered = allCours.filter((cours) => {
      if (selectedClass && !cours.id_classes?.includes(selectedClass.id)) return false;
      const assignments = (cours.class_assignments || []).filter((item) => !selectedClass || item.id_class === selectedClass.id);
      if (selectedPeriod === "without-ue" && !assignments.some((item) => !item.id_ue)) return false;
      if (selectedPeriod !== "all" && selectedPeriod !== "without-ue" && !assignments.some((item) => item.ue?.id_evaluationPeriod === selectedPeriod)) return false;
      const haystack = `${cours.Subject?.label || ""} ${cours.TeacherProfil?.firstname || ""} ${cours.TeacherProfil?.lastname || ""}`.toLowerCase();
      return haystack.includes(searchTerm.toLowerCase());
    });
    setFilteredCours(filtered);
  }, [selectedClass, selectedPeriod, searchTerm, allCours]);

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          {/* Page Header */}
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">Gestion des Cours </h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.timeTableList}>Emploi du Temps</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Cours
                  </li>
                </ol>
              </nav>
            </div>
            {/* Bouton en entête de la page */}
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              {/* <TooltipOption updateData={reloadData} /> */}
              <ProtectedBtnAddCours setShowCreate={setShowCreate} />
            </div>
          </div>
          {/* /Page Header */}
          {!selectedYear?.statut && <InfosAlertYearDisabled />}

          <div className="row g-3 align-items-stretch" style={{ height: "calc(100vh - 190px)", minHeight: 480 }}>
            <div className="col-xl-3 col-lg-4 h-100">
              <div className="card mb-0 h-100 overflow-hidden">
                <div className="card-header py-3"><h5 className="mb-0">Classes</h5></div>
                <div className="list-group list-group-flush flex-grow-1" style={{ overflowY: "auto" }}>
                  <button type="button" onClick={() => setSelectedClass(undefined)} className={`list-group-item list-group-item-action d-flex justify-content-between align-items-center ${!selectedClass ? "active" : ""}`}>
                    <span>Toutes les classes</span><span className="badge bg-light text-dark">{allCours.length}</span>
                  </button>
                  {allClass.map((classe) => (
                    <button type="button" key={classe.id} onClick={() => setSelectedClass(classe)} className={`list-group-item list-group-item-action d-flex justify-content-between align-items-center ${selectedClass?.id === classe.id ? "active" : ""}`}>
                      <span className="text-truncate me-2">{classe.label}</span>
                      <span className={`badge ${selectedClass?.id === classe.id ? "bg-white text-primary" : "bg-light text-dark"}`}>
                        {allCours.filter((course) => course.id_classes?.includes(classe.id)).length}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="col-xl-9 col-lg-8 h-100 d-flex flex-column">
              <div className="d-flex gap-2 mb-3 flex-shrink-0">
              <div className="search-box flex-grow-1">
              <input
                type="text"
                className="form-control"
                placeholder="Rechercher par matière ou professeur..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              </div>
              <Select style={{ width: 220 }} value={selectedPeriod} onChange={setSelectedPeriod}
                options={[
                  { value: "all", label: "Tous les semestres" },
                  ...periods.map((period) => ({ value: period.id, label: period.label })),
                  { value: "without-ue", label: "Sans UE / sans semestre" },
                ]} />
              </div>
          <div className="flex-grow-1 pe-1" style={{ overflowY: "auto", minHeight: 0 }}>
          {isLoading && !hasLoaded ? (
            <div className="text-center">
              <div className="spinner-border" role="status"></div>
              <span className="visually">Chargement des Cours...</span>
            </div>
          ) : (
            <div className="row g-3">
              {filteredCours.map((cours) => {
                const visibleAssignment = selectedClass
                  ? cours.class_assignments?.find((item) => item.id_class === selectedClass.id)
                  : cours.class_assignments?.find((item) => item.id_ue) || cours.class_assignments?.[0];
                return (
                <div key={cours.id} className="col-12 col-xxl-6">
                  <div className="card h-100 border shadow-sm mb-0">
                    <div className="card-body p-3">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                          <div className="overflow-hidden pe-2">
                            <h5
                              className="card-title mb-0 text-truncate"
                            >
                              {cours.Subject.label}
                            </h5>
                          </div>

                        <div className="dropdown">
                          <button
                            className="btn text-dark p-0"
                            data-bs-toggle="dropdown"
                          >
                            <i className="ti ti-dots-vertical fs-4"></i>
                          </button>
                          <ul className="dropdown-menu">
                            <ProtectedBtnUpdateCours
                              setSelectedCoursId={setSelectedCoursId}
                              setShowEdit={setShowEdit}
                              record={cours}
                            />
                            <ProtectedBtnDeleteCours
                              showConfirmationAlert={showConfirmationAlert}
                              record={cours}
                            />
                            <li>
                              <Link
                                to="#"
                                className="dropdown-item"
                                onClick={() => {
                                  setShowSupport(true);
                                  setSelectedCoursId(cours?.id);
                                }}
                              >
                                <i className="ti ti-eye me-2"></i>
                                Voire les supports
                              </Link>
                            </li>
                          </ul>
                        </div>
                      </div>

                      <div className="small text-muted mb-2">
                          <i className="ti ti-user me-1" />
                          <span className="text-dark">
                            {cours.TeacherProfil?.firstname}{" "}
                            {cours.TeacherProfil?.lastname}
                          </span>
                      </div>
                      <div className="small text-muted mb-3">
                          <i className="ti ti-door me-1" /> Salle par défaut : <span className="text-dark">
                            {cours?.id_classroom
                              ? allClassroom.find(
                                  (c) => c.id === cours.id_classroom,
                                )?.label
                              : "Non définie"}
                          </span>
                      </div>
                      <div className="d-flex gap-1 flex-wrap mb-3">
                        {(cours.classes || []).map((classe) => (
                          <span className="badge bg-light text-dark border" key={classe.id}>
                            {classe.levelAbbreviation} - {classe.abbreviation}
                          </span>
                        ))}
                      </div>
                      <div className="d-flex gap-2 flex-wrap">
                        <span className={`badge ${cours.status === "Approuvé" ? "bg-success" : "bg-warning text-dark"}`}>
                          <i className={`ti ${cours.status === "Approuvé" ? "ti-circle-check" : "ti-clock"} me-1`} />{cours.status}
                        </span>
                        <span className={`badge ${visibleAssignment?.id_ue ? "bg-primary" : "bg-light text-muted border"}`}>
                          <i className={`ti ${visibleAssignment?.id_ue ? "ti-link" : "ti-unlink"} me-1`} />
                          {visibleAssignment?.id_ue ? `${visibleAssignment.ue?.codeUE || "UE"} · ${visibleAssignment.credit} cr.` : "Sans UE"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )})}
              {!filteredCours.length && <div className="col-12"><div className="text-center text-muted border rounded-3 bg-white py-5">Aucun cours pour cette classe.</div></div>}
            </div>
          )}
          </div>
            </div>
          </div>
        </div>
        {/* /Page Header */}

        <EditCoursModal
          show={showEdit}
          coursID={selectedCoursId}
          onClose={() => setShowEdit(false)}
          onSaved={reloadData}
        />
        <CreateCoursModal
          show={showCreate}
          onClose={() => setShowCreate(false)}
          onSaved={reloadData}
        />

        <CourseSupportList
          show={showSupport}
          onClose={() => setShowSupport(false)}
          course={allCours.find((c) => c.id === selectedCoursId)}
        />
      </div>
    </>
  );
};

export default CoursList;
